import type { DealBoardComment, DealBoardLookbook } from "@/api/deal-board-comments-api";
import type { DealTransformed } from "@/lib/schema/deal-schema";

/*
 * The Deal Wall shows two things that were never one list before: the
 * workspace's own deal notes, and the comments people leave on the deal out on
 * the Raiseboard — the public board, the global board, and each lookbook it
 * sits in. This module merges them and answers "where did this come from",
 * which is the only question the UI has to get right.
 *
 * A source is the audience, not the keyboard. That distinction matters for one
 * case: a comment written from this page (origin "deal_profile") files under
 * the thread it landed in, because that is who can actually read it.
 */

export type WallSource =
  | "internal"
  | "public_board"
  | "global_board"
  | `lookbook:${string}`;

type WallEntryBase = {
  id: string;
  author: string;
  date: Date;
  text: string;
  source: WallSource;
  sourceLabel: string;
};

export type WallEntry =
  | (WallEntryBase & {
      kind: "note";
      source: "internal";
      authorId?: string;
      tags: string[];
    })
  | (WallEntryBase & {
      kind: "board";
      canDelete: boolean;
    });

export const INTERNAL_SOURCE_LABEL = "Internal note";

/** Notes the system writes to correlate a doc with a Gap Analysis question. */
const SYSTEM_NOTE_TAG_PREFIX = "gap-fill-doc:";

function safeDate(value: string): Date | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A board comment's bucket. Lookbook scope wins over origin: a comment posted
 * into a lookbook from the deal profile is visible exactly where the lookbook's
 * own comments are, so that is where it belongs.
 */
export function sourceOfComment(comment: DealBoardComment): WallSource {
  if (comment.lookbook_id) return `lookbook:${comment.lookbook_id}`;
  return comment.origin === "global_board" ? "global_board" : "public_board";
}

export function labelOfComment(comment: DealBoardComment): string {
  if (comment.lookbook_id) {
    return comment.lookbook_name
      ? `Lookbook: ${comment.lookbook_name}`
      : "Lookbook";
  }
  return comment.origin === "global_board" ? "Global board" : "Public board";
}

/**
 * Every source that could be filtered on, in a stable order: the internal
 * notes, the two public threads, then each lookbook holding this deal.
 *
 * Built from the lookbooks the server says exist rather than from the comments,
 * so an empty lookbook thread is still offered — filtering to it and seeing
 * nothing is a real answer. A lookbook the deal has since been removed from has
 * no entry here, so its orphaned comments are added back from the comment list.
 */
export function sourceOptions(
  comments: DealBoardComment[],
  lookbooks: DealBoardLookbook[]
): Array<{ value: WallSource; label: string }> {
  const options: Array<{ value: WallSource; label: string }> = [
    { value: "internal", label: INTERNAL_SOURCE_LABEL },
    { value: "public_board", label: "Public board" },
    { value: "global_board", label: "Global board" },
    ...lookbooks.map((l) => ({
      value: `lookbook:${l._id}` as WallSource,
      label: `Lookbook: ${l.name}`,
    })),
  ];

  const known = new Set(options.map((o) => o.value));
  for (const comment of comments) {
    const source = sourceOfComment(comment);
    if (known.has(source)) continue;
    known.add(source);
    options.push({ value: source, label: labelOfComment(comment) });
  }

  return options;
}

/**
 * The merged feed, newest first. Entries with an unparseable date are dropped
 * rather than sorted arbitrarily, matching the audit log's handling.
 */
export function buildWallEntries(
  dealNotes: DealTransformed["dealNotes"],
  comments: DealBoardComment[]
): WallEntry[] {
  const entries: WallEntry[] = [];

  for (const note of dealNotes ?? []) {
    // System-generated doc correlations aren't commentary — they'd bury the
    // discussion (SAAS-602).
    if (note.tags?.some((tag) => tag.startsWith(SYSTEM_NOTE_TAG_PREFIX))) {
      continue;
    }
    const date = safeDate(note.created_on);
    if (!date) continue;
    entries.push({
      kind: "note",
      id: note._id,
      author: note.created_by_user,
      authorId: note.created_by_user_id,
      date,
      text: note.note,
      tags: note.tags ?? [],
      source: "internal",
      sourceLabel: INTERNAL_SOURCE_LABEL,
    });
  }

  for (const comment of comments) {
    const date = safeDate(comment.created_on);
    if (!date) continue;
    entries.push({
      kind: "board",
      id: comment._id,
      // A guest's name is unverified, so it is never shown bare.
      author: comment.guest
        ? comment.user_name && comment.user_name !== "Guest"
          ? `${comment.user_name} (guest)`
          : "Guest"
        : comment.user_name || "Unknown user",
      date,
      text: comment.text,
      source: sourceOfComment(comment),
      sourceLabel: labelOfComment(comment),
      canDelete: comment.can_delete,
    });
  }

  return entries.sort((a, b) => b.date.getTime() - a.date.getTime());
}

/**
 * Applies both filters. An empty selection means "everything", so neither
 * filter narrows anything until it is used.
 *
 * Tags only exist on internal notes, so a tag filter necessarily hides every
 * board comment — that is the honest reading of "show me notes tagged Team",
 * and leaving them in would be a filter that silently doesn't apply.
 */
export function filterWallEntries(
  entries: WallEntry[],
  sources: WallSource[],
  tags: string[]
): WallEntry[] {
  return entries.filter((entry) => {
    if (sources.length > 0 && !sources.includes(entry.source)) return false;
    if (tags.length === 0) return true;
    return entry.kind === "note" && entry.tags.some((t) => tags.includes(t));
  });
}
